import React, { useState, useEffect } from 'react';
import { 
  Tag, 
  Plus, 
  Trash2, 
  ToggleLeft, 
  ToggleRight,
  Loader2,
  Percent,
  Euro,
  Users
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDiscounts = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDiscount, setNewDiscount] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_people: null,
    max_uses: null,
    valid_from: '',
    valid_until: ''
  });

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API}/admin/discounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiscounts(response.data);
    } catch (err) {
      console.error('Error fetching discounts:', err);
      toast.error('Error al cargar los descuentos');
    } finally {
      setLoading(false);
    }
  };

  const createDiscount = async () => {
    if (!newDiscount.code || !newDiscount.discount_value) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${API}/admin/discounts`, newDiscount, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Código de descuento creado');
      setShowCreateModal(false);
      setNewDiscount({
        code: '',
        discount_type: 'percentage',
        discount_value: 10,
        min_people: null,
        max_uses: null,
        valid_from: '',
        valid_until: ''
      });
      fetchDiscounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear el descuento');
    }
  };

  const toggleDiscount = async (code) => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.put(`${API}/admin/discounts/${code}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Estado actualizado');
      fetchDiscounts();
    } catch (err) {
      toast.error('Error al actualizar el estado');
    }
  };

  const deleteDiscount = async (code) => {
    if (!window.confirm('¿Estás seguro de eliminar este código?')) return;
    
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API}/admin/discounts/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Código eliminado');
      fetchDiscounts();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-[#C2410C]" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-discounts">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-[#1C1917]">Descuentos</h1>
          <p className="text-[#57534E]">Gestiona códigos promocionales y de grupo</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-[#C2410C] hover:bg-[#9A3412] rounded-full"
          data-testid="create-discount-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Crear Código
        </Button>
      </div>

      {/* Discount Types Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="bg-gradient-to-br from-[#C2410C]/10 to-[#C2410C]/5 border-[#C2410C]/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <Percent className="h-6 w-6 text-[#C2410C]" />
              <h3 className="font-semibold">Porcentaje</h3>
            </div>
            <p className="text-sm text-[#57534E]">
              Descuento en % sobre el precio total. Ej: VERANO20 = 20% dto.
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-[#F59E0B]/10 to-[#F59E0B]/5 border-[#F59E0B]/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <Euro className="h-6 w-6 text-[#F59E0B]" />
              <h3 className="font-semibold">Cantidad Fija</h3>
            </div>
            <p className="text-sm text-[#57534E]">
              Descuento en € sobre el precio. Ej: PROMO10 = 10€ dto.
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="h-6 w-6 text-green-600" />
              <h3 className="font-semibold">Grupos</h3>
            </div>
            <p className="text-sm text-[#57534E]">
              Requiere mínimo de personas. Ej: GRUPO10 = 15% dto. mín. 10 personas.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Discounts List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-[#C2410C]" />
            Códigos de Descuento
          </CardTitle>
        </CardHeader>
        <CardContent>
          {discounts.length === 0 ? (
            <p className="text-center text-[#57534E] py-8">
              No hay códigos de descuento. Crea el primero.
            </p>
          ) : (
            <div className="space-y-4">
              {discounts.map((discount) => (
                <div
                  key={discount.code}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    discount.is_active 
                      ? 'bg-white border-[#E5E5E5]' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      discount.discount_type === 'percentage' 
                        ? 'bg-[#C2410C]/10' 
                        : 'bg-[#F59E0B]/10'
                    }`}>
                      {discount.discount_type === 'percentage' 
                        ? <Percent className="h-6 w-6 text-[#C2410C]" />
                        : <Euro className="h-6 w-6 text-[#F59E0B]" />
                      }
                    </div>
                    <div>
                      <p className="font-mono font-bold text-lg">{discount.code}</p>
                      <p className="text-sm text-[#57534E]">
                        {discount.discount_value}{discount.discount_type === 'percentage' ? '%' : '€'} de descuento
                        {discount.min_people && ` (mín. ${discount.min_people} personas)`}
                      </p>
                      {discount.valid_until && (
                        <p className="text-xs text-[#57534E]">
                          Válido hasta: {discount.valid_until}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {discount.current_uses}{discount.max_uses ? `/${discount.max_uses}` : ''} usos
                      </p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        discount.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {discount.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleDiscount(discount.code)}
                        title={discount.is_active ? 'Desactivar' : 'Activar'}
                      >
                        {discount.is_active 
                          ? <ToggleRight className="h-6 w-6 text-green-600" />
                          : <ToggleLeft className="h-6 w-6 text-gray-400" />
                        }
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteDiscount(discount.code)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Discount Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Crear Código de Descuento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="code">Código *</Label>
              <Input
                id="code"
                value={newDiscount.code}
                onChange={(e) => setNewDiscount({...newDiscount, code: e.target.value.toUpperCase()})}
                placeholder="VERANO20"
                className="uppercase"
                data-testid="discount-code-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="type">Tipo</Label>
                <Select
                  value={newDiscount.discount_type}
                  onValueChange={(value) => setNewDiscount({...newDiscount, discount_type: value})}
                >
                  <SelectTrigger data-testid="discount-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                    <SelectItem value="fixed">Cantidad Fija (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="value">Valor *</Label>
                <Input
                  id="value"
                  type="number"
                  value={newDiscount.discount_value}
                  onChange={(e) => setNewDiscount({...newDiscount, discount_value: parseFloat(e.target.value)})}
                  placeholder="10"
                  data-testid="discount-value-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="min_people">Mín. Personas (Grupos)</Label>
                <Input
                  id="min_people"
                  type="number"
                  value={newDiscount.min_people || ''}
                  onChange={(e) => setNewDiscount({...newDiscount, min_people: e.target.value ? parseInt(e.target.value) : null})}
                  placeholder="10"
                />
              </div>
              <div>
                <Label htmlFor="max_uses">Máx. Usos</Label>
                <Input
                  id="max_uses"
                  type="number"
                  value={newDiscount.max_uses || ''}
                  onChange={(e) => setNewDiscount({...newDiscount, max_uses: e.target.value ? parseInt(e.target.value) : null})}
                  placeholder="100"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="valid_from">Válido Desde</Label>
                <Input
                  id="valid_from"
                  type="date"
                  value={newDiscount.valid_from}
                  onChange={(e) => setNewDiscount({...newDiscount, valid_from: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="valid_until">Válido Hasta</Label>
                <Input
                  id="valid_until"
                  type="date"
                  value={newDiscount.valid_until}
                  onChange={(e) => setNewDiscount({...newDiscount, valid_until: e.target.value})}
                />
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={createDiscount}
                className="flex-1 bg-[#C2410C] hover:bg-[#9A3412]"
                data-testid="save-discount-btn"
              >
                Crear Código
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDiscounts;
